/**
 * Catálogo de etiquetas de origen (Sales_Origin_Tags) + selección multi.
 * Default: todas las etiquetas Meta clasificadas.
 * Orden y grupos = Configuración → Orígenes (order + isMeta).
 * @module-isolated analytics-shared
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { fetchOriginTags } from '../../services/analytics-api.js';
import { getIdToken } from '../../services/firebase-auth.js';
import { useAnalyticsRefresh } from './useAnalyticsRefresh.js';

export const ORIGIN_NONE = '__none__';
export const ORIGIN_UNCLASSIFIED = '__unclassified__';

const catalogMemory = { items: null, loadedAt: 0 };

function sortByConfigOrder_(list) {
  return [...(list || [])].sort((a, b) => {
    const ao = Number(a.order);
    const bo = Number(b.order);
    const aOk = !Number.isNaN(ao);
    const bOk = !Number.isNaN(bo);
    if (aOk && bOk && ao !== bo) return ao - bo;
    if (aOk && !bOk) return -1;
    if (!aOk && bOk) return 1;
    return String(a.tagName || '').localeCompare(String(b.tagName || ''), 'es');
  });
}

function metaDefaults_(items) {
  const meta = sortByConfigOrder_(items || [])
    .filter((t) => t && t.active !== false && t.isMeta === true && t.isOrigin !== false)
    .map((t) => String(t.tagName));
  if (meta.length) return meta;
  // Sin Meta clasificadas aún: todas las etiquetas origen clasificadas (evita 0 leads).
  return sortByConfigOrder_(items || [])
    .filter(
      (t) =>
        t &&
        t.active !== false &&
        t.isOrigin !== false &&
        (t.isMeta === true || t.isMeta === false)
    )
    .map((t) => String(t.tagName));
}

/**
 * @param {{ persistKey?: string, enabled?: boolean }} [opts]
 */
export function useOriginTagsCatalog(opts) {
  const persistKey = (opts && opts.persistKey) || '';
  const enabled = opts && opts.enabled === false ? false : true;
  const [items, setItems] = useState(() => catalogMemory.items || []);
  const [loading, setLoading] = useState(!catalogMemory.items);
  const [error, setError] = useState('');
  const [selected, setSelectedState] = useState(null); // null = aún no inicializado
  const [ready, setReady] = useState(false);

  const load = useCallback(async (force) => {
    if (!enabled) return;
    setLoading(true);
    setError('');
    try {
      const token = await getIdToken(false);
      const res = await fetchOriginTags(token, {});
      const list = sortByConfigOrder_(
        Array.isArray(res && res.items)
          ? res.items
          : Array.isArray(res && res.tags)
            ? res.tags
            : []
      );
      catalogMemory.items = list;
      catalogMemory.loadedAt = Date.now();
      setItems(list);
      setSelectedState((prev) => {
        if (!force && Array.isArray(prev)) return prev;
        if (persistKey) {
          try {
            const raw = sessionStorage.getItem('analytics:originTags:' + persistKey);
            if (raw) {
              const parsed = JSON.parse(raw);
              if (Array.isArray(parsed)) return parsed;
            }
          } catch (eMem) {
            /* ignore */
          }
        }
        return metaDefaults_(list);
      });
      setReady(true);
    } catch (err) {
      setError((err && err.message) || 'No se pudieron cargar etiquetas de origen.');
      setItems([]);
      setSelectedState([]);
      setReady(true);
    } finally {
      setLoading(false);
    }
  }, [enabled, persistKey]);

  useAnalyticsRefresh(
    useCallback(() => {
      if (!enabled) return;
      catalogMemory.items = null;
      catalogMemory.loadedAt = 0;
      load(true);
    }, [enabled, load])
  );

  useEffect(() => {
    if (!enabled) {
      setReady(true);
      return;
    }
    if (catalogMemory.items && catalogMemory.items.length) {
      const list = sortByConfigOrder_(catalogMemory.items);
      setItems(list);
      setSelectedState((prev) => {
        if (Array.isArray(prev)) return prev;
        if (persistKey) {
          try {
            const raw = sessionStorage.getItem('analytics:originTags:' + persistKey);
            if (raw) {
              const parsed = JSON.parse(raw);
              if (Array.isArray(parsed)) return parsed;
            }
          } catch (eMem) {
            /* ignore */
          }
        }
        return metaDefaults_(list);
      });
      setLoading(false);
      setReady(true);
      return;
    }
    load(false);
  }, [enabled, load, persistKey]);

  const setSelected = useCallback(
    (next) => {
      const arr = Array.isArray(next) ? next.map(String) : [];
      setSelectedState(arr);
      if (persistKey) {
        try {
          sessionStorage.setItem('analytics:originTags:' + persistKey, JSON.stringify(arr));
        } catch (eSet) {
          /* ignore */
        }
      }
    },
    [persistKey]
  );

  const selectMetaOnly = useCallback(() => {
    setSelected(metaDefaults_(items));
  }, [items, setSelected]);

  const selectAll = useCallback(() => {
    const names = sortByConfigOrder_(items || [])
      .filter(
        (t) =>
          t &&
          t.active !== false &&
          t.isOrigin !== false &&
          (t.isMeta === true || t.isMeta === false)
      )
      .map((t) => String(t.tagName));
    setSelected(names.concat([ORIGIN_NONE, ORIGIN_UNCLASSIFIED]));
  }, [items, setSelected]);

  const clearAll = useCallback(() => {
    setSelected([]);
  }, [setSelected]);

  const options = useMemo(() => {
    const meta = [];
    const other = [];
    let hasSinEtiquetaClassified = false;
    sortByConfigOrder_(items || []).forEach((t) => {
      if (!t || t.active === false) return;
      if (t.isOrigin === false) return;
      if (t.isMeta !== true && t.isMeta !== false) return; // solo clasificadas como en Config
      const name = String(t.tagName);
      const isNo =
        t.isNoTag === true ||
        name.trim().toLowerCase() === 'sin etiqueta' ||
        name.trim().toLowerCase() === '__none__';
      if (isNo) hasSinEtiquetaClassified = true;
      const opt = {
        id: name,
        label: isNo ? 'Sin Etiqueta (sin tag en Kommo)' : name,
        isMeta: t.isMeta === true,
        order: Number(t.order) || 0,
        group: t.isMeta === true ? 'Meta' : 'No Meta'
      };
      if (t.isMeta === true) meta.push(opt);
      else other.push(opt);
    });
    const extras = [
      {
        id: ORIGIN_UNCLASSIFIED,
        label: 'Sin clasificar (todas)',
        isMeta: null,
        order: 9000,
        group: 'Otros'
      }
    ];
    // Si «Sin Etiqueta» ya está clasificada en Config, no duplicar el token __none__.
    if (!hasSinEtiquetaClassified) {
      extras.push({
        id: ORIGIN_NONE,
        label: 'Sin etiqueta',
        isMeta: null,
        order: 9001,
        group: 'Otros'
      });
    }
    return meta.concat(other).concat(extras);
  }, [items]);

  const selectedSafe = Array.isArray(selected) ? selected : [];

  return {
    items,
    options,
    selected: selectedSafe,
    setSelected,
    selectMetaOnly,
    selectAll,
    clearAll,
    loading,
    error,
    ready,
    reload: () => load(true),
    originTagsParam: selectedSafe
  };
}
